import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import api from "../services/api";
import { unwrapOpsData } from "../utils/opsApiPayload";
import { showToast } from "../utils/toast";
import { USER_FACING_ERRORS } from "../utils/userFacingErrors";

const PlatformConfigContext = createContext({
  config: null,
  loading: true,
  isFeatureEnabled: () => true,
  isMaintenanceMode: () => false,
  isRegistrationEnabled: () => true,
  isRatingsEnabled: () => true,
  isModuleEnabled: () => true,
  getContent: () => ({}),
});

export function PlatformConfigProvider({ children }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const maintenanceToastShownRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const res = await api.getPlatformConfig();
        if (!cancelled) setConfig(unwrapOpsData(res) || res?.config || null);
      } catch {
        if (!cancelled) setConfig(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (loading || !config?.adminSystem?.maintenanceMode) return;
    const path = String(window.location.pathname || "");
    if (/^\/(login|admin|ops)(\/|$)/i.test(path)) return;
    if (maintenanceToastShownRef.current) return;
    maintenanceToastShownRef.current = true;
    showToast.info(USER_FACING_ERRORS.MAINTENANCE_MODE.message);
  }, [loading, config?.adminSystem?.maintenanceMode]);

  const value = useMemo(
    () => ({
      config,
      loading,
      isFeatureEnabled: (key) => {
        const flags = config?.featureFlags || config?.features || config?.flags || {};
        if (flags[key] === undefined || flags[key] === null) return true;
        return Boolean(flags[key]);
      },
      isMaintenanceMode: () => Boolean(config?.adminSystem?.maintenanceMode),
      isRegistrationEnabled: () => config?.adminSystem?.newRegistrations !== false,
      isRatingsEnabled: () => config?.adminSystem?.ratingsEnabled !== false,
      isModuleEnabled: (moduleId, roleId = "customer") => {
        const modules = config?.adminSystem?.modules || {};
        if (moduleId && modules[moduleId] === false) return false;
        const visibility = config?.adminSystem?.visibility?.[moduleId];
        if (visibility && roleId && visibility[roleId] === false) return false;
        return true;
      },
      getContent: () => config?.content || {},
      bookingSettings: () => config?.bookingSettings || {},
    }),
    [config, loading]
  );

  return <PlatformConfigContext.Provider value={value}>{children}</PlatformConfigContext.Provider>;
}

export function usePlatformConfig() {
  return useContext(PlatformConfigContext);
}
